import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { api } from '../api/http.js';
import { useUI } from '../ui/UIProvider.jsx';
import { CountryFlag } from '../components/CountryFlag.jsx';
import { lookup as centroidFor } from '../data/countryCentroids.js';

/**
 * User Map. Plots NotesHQ sign-ups on a Leaflet world map. Each
 * country with a known sign-up gets a circle marker at its centroid,
 * sized by user count and tinted on the NotesHQ purple ramp.
 *
 * Tile source: CartoDB Voyager — a free OSM-derived basemap that
 * looks deliberately understated (cream landmass, muted labels) so
 * the data markers stay the focal point. Attribution is required and
 * lives at the bottom of the map automatically via Leaflet.
 *
 * Layout: 2-column grid (map + right rail). The rail carries a small
 * KPI card and a Top Countries list sorted by user count, both with
 * country flags. Below 1000px we stack the rail under the map.
 *
 * Server contract: GET /api/admin/stats/geo → { "GB": 247, "US": 489 }.
 * Countries not in our centroids table are silently skipped (no
 * point plotting a marker we can't position); they still show up
 * in the rail's "Top Countries" list.
 *
 * @author <lukman@zshift.io>
 */
export function Map() {
  const { toast } = useUI();
  const [byCountry, setByCountry] = useState({});
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api('/api/admin/stats/geo');
        if (cancelled) return;
        setByCountry(data && typeof data === 'object' ? data : {});
      } catch (e) {
        if (!cancelled) toast.error(e?.message || 'Could not load country data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sorted rows for the right rail + max-count for marker scaling.
  // Memoised so the map's render path doesn't recompute on every
  // re-render (Leaflet re-renders are expensive).
  const rows = useMemo(() => {
    return Object.entries(byCountry)
      .map(([iso, count]) => ({ iso, count: Number(count) || 0 }))
      .filter((r) => r.iso && r.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [byCountry]);

  const total = useMemo(() => rows.reduce((sum, r) => sum + r.count, 0), [rows]);
  const maxCount = rows[0]?.count || 1;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">User <em>map</em></h1>
          <p className="page-sub">
            Where NotesHQ accounts have signed up from — country distribution at a glance.
            Tiles are OpenStreetMap data via CartoDB.
          </p>
        </div>
      </div>

      <div className="map-grid">
        {/* ── Map column ─────────────────────────────────────── */}
        <div className="card map-card">
          <MapContainer
            center={[25, 15]}
            zoom={2}
            minZoom={2}
            maxZoom={6}
            worldCopyJump
            scrollWheelZoom
            zoomControl={false}
            style={{ width: '100%', height: '100%' }}
            attributionControl
          >
            <TileLayer
              // CartoDB Voyager — labelled, cream landmass, very light
              // grey water; reads as a "designed" basemap rather than
              // a raw OSM tile. Free for any use with attribution.
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              subdomains="abcd"
              maxZoom={19}
            />
            <ZoomControl position="bottomright" />

            {rows.map((r) => {
              const ll = centroidFor(r.iso);
              if (!ll) return null;
              // Marker radius scales by sqrt of count so visual area
              // grows linearly with the data. Clamped so single-user
              // countries are still hoverable, and the biggest never
              // dominates the basemap.
              const radius = Math.max(6, Math.min(28, 6 + 18 * Math.sqrt(r.count / maxCount)));
              // Display label — same number used in the permanent
              // tooltip on the marker. Compact form (1.2K, 12K) when
              // the count is big enough to spill out of the bubble.
              const label = compactCount(r.count);
              return (
                <CircleMarker
                  key={r.iso}
                  center={ll}
                  radius={radius}
                  pathOptions={{
                    color:     '#4A1FB0',
                    weight:    1.5,
                    fillColor: '#6B3FD4',
                    fillOpacity: 0.55,
                  }}
                >
                  {/* Permanent label — the number floats centered on the
                      marker so users see counts at a glance without
                      hovering. The .map-marker-label class strips
                      Leaflet's default tooltip chrome (background,
                      border, drop shadow) and lays just the number on
                      top in white. */}
                  <Tooltip
                    permanent
                    direction="center"
                    className="map-marker-label"
                    opacity={1}
                  >
                    {label}
                  </Tooltip>
                  {/* Hover tooltip with the full breakdown — ISO code +
                      exact count. Distinct className so it gets its own
                      (chrome'd) styling on top of the permanent label. */}
                  <Tooltip
                    direction="top"
                    offset={[0, -radius - 2]}
                    opacity={0.96}
                    sticky
                  >
                    <strong>{r.iso}</strong> · {r.count.toLocaleString()} user{r.count === 1 ? '' : 's'}
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>

        {/* ── Right rail ─────────────────────────────────────── */}
        <div className="map-rail">
          <div className="card">
            <div className="card-head">
              <h3>Reach</h3>
            </div>
            <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <KpiCell label="Total users"  value={total.toLocaleString()} loading={loading} />
              <KpiCell label="Countries"    value={rows.length.toLocaleString()} loading={loading} />
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Top countries</h3>
              <div className="sub">By sign-up</div>
            </div>
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Country</th>
                    <th style={{ textAlign: 'right' }}>Users</th>
                    <th style={{ width: '38%' }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>
                      No country data yet — users signed up before country was captured won't show here.
                    </td></tr>
                  )}
                  {!loading && rows.slice(0, 12).map((r, i) => (
                    <tr key={r.iso}>
                      <td className="mono" style={{ color: 'var(--ink-3)' }}>{i + 1}</td>
                      <td>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <CountryFlag code={r.iso} width={18} />
                          <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>{r.iso}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.count.toLocaleString()}</td>
                      <td>
                        <div className="map-bar-wrap" aria-hidden="true">
                          <div
                            className="map-bar"
                            style={{ width: `${Math.max(2, (r.count / maxCount) * 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && rows.length > 12 && (
              <div style={{ padding: '10px 16px', borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink-3)' }}>
                + {(rows.length - 12).toLocaleString()} more countries
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Render the count as a short string that fits inside a small circle:
 *   < 1000  → as-is        ("247")
 *   < 10000 → one-decimal K ("1.2K", "9.9K")
 *   ≥ 10000 → no decimal K  ("12K", "184K")
 *   ≥ 1M    → M form        ("1.2M", "12M")
 *
 * Conservative thresholds — at NotesHQ's scale most numbers will fit
 * as-is, but country totals can spike (e.g. US sign-ups during a launch
 * week) and the compact form prevents the label from overflowing the
 * marker.
 */
function compactCount(n) {
  if (n < 1000)    return String(n);
  if (n < 10000)   return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (n < 1_000_000) return Math.round(n / 1000) + 'K';
  if (n < 10_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  return Math.round(n / 1_000_000) + 'M';
}

// ── KPI cell (small, used on the right rail) ────────────────────
function KpiCell({ label, value, loading }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', fontFamily: 'Geist Mono, monospace' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
        {loading ? '—' : value}
      </div>
    </div>
  );
}
